import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import Button from "@/components/button"
import Bottombar from "@/components/bottombar"
import {
  BreakdownFormData,
  FormParamStatus,
  isSubmittedLikeStatus,
} from "@/lib/form/types"
import { FormEvent, useEffect, useState } from "react"
import { UseFormReturn } from "@/lib/form/form"
import useStore from "@/store"
import { toast } from "sonner"

export type RestartMachineWorkProcessStoreKey = "ipqc" | "boilout"

export type RestartMachineWorkSheetProps = {
  form: UseFormReturn
  processStoreKey?: RestartMachineWorkProcessStoreKey
}

export default function RestartMachineWorkSheet({
  form,
  processStoreKey = "ipqc",
}: RestartMachineWorkSheetProps) {
  const [open, setOpen] = useState(false)
  const [path, setPath] = useState("")

  useInit()

  function useInit() {
    useEffect(() => {
      form.addEventHandler("restartMachineWork", (__event, ctx) => {
        if (isCurrentLineSubmittedLike()) {
          setOpen(false)
          toast.error("Cannot restart breakdown on a submitted sheet.")
          return
        }

        setPath(ctx.path)
        setOpen(true)
      })

      return () => {
        form.removeEventHandler("restartMachineWork")
      }
    }, [form])
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  function isCurrentLineSubmittedLike(): boolean {
    const processStore = useStore.getState()[processStoreKey] as {
      getCurrLineStatus(): FormParamStatus
    }

    return isSubmittedLikeStatus(processStore.getCurrLineStatus())
  }

  async function handleRestart(e: FormEvent<HTMLFormElement>) {
    e.stopPropagation()
    e.preventDefault()

    if (isCurrentLineSubmittedLike()) {
      close()
      toast.error("Cannot restart breakdown on a submitted sheet.")
      return
    }

    const breakdownPath = `${path}.breakdown`
    const breakdowns: BreakdownFormData[] = form.getValues(breakdownPath) || []

    if (!breakdowns.length) {
      close()

      return
    }

    const lastBreakdown = breakdowns.at(-1)
    if (!lastBreakdown) {
      close()

      return
    }

    if (lastBreakdown?.endedAt) {
      close()

      return
    }

    setIsSubmitting(true)

    try {
      const updatedBreakdowns = breakdowns.map((breakdown, index) => {
        if (index !== breakdowns.length - 1) {
          return breakdown
        }

        return {
          ...breakdown,
          endedAt: new Date().toISOString(),
        }
      })

      form.setValue(breakdownPath, updatedBreakdowns, {
        shouldDirty: true,
        shouldTouch: true,
      })

      close()
    } catch (err) {
      console.error("Failed to save breakdown locally:", err)
      toast.error("Failed to save breakdown locally. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  function close() {
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        title="Fill remarks on stage"
        className="shadow-drop-shadow rounded-t-lg"
      >
        <form onSubmit={handleRestart}>
          <SheetHeader>
            <SheetTitle className="text-xl">Restart Work</SheetTitle>
          </SheetHeader>

          <SheetDescription>
            Restart work marks the end of stage breakdown. By restarting you are
            confirming that the stage is working okay now.
          </SheetDescription>

          <Bottombar className="flex gap-4 bg-white">
            <Button variant="outline" onClick={close} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="default"
              className="flex-1"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Ending..." : "End Breakdown"}
            </Button>
          </Bottombar>
        </form>
      </SheetContent>
    </Sheet>
  )
}
